import React, { useEffect, useRef, useState } from "react";
import styles from "./QuarryDetailsCard.module.scss";
import {
  getFormattedTimes,
  getGoogleMapsUrl,
  getIsClosedDay,
  getTodaysDateTime,
  quarryDetailsConts,
} from "./constants";
import notesIcon from "../../../../assets/images/notes-icon.svg";
import editIcon from "../../../../assets/images/edit-icon-outline.svg";
import locationIcon from "../../../../assets/images/grey-marker.svg";
import phoneIcon from "../../../../assets/images/phone-outline.svg";
import clockIcon from "../../../../assets/images/clock-outline.svg";
import downArrow from "../../../../assets/images/dropdown-arrow.svg";
import { PlacementPositions } from "../../../../enums/PlacementPositionsEnums";
import { PopoverTrigger } from "../../../../enums/PopoverEnums";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import { Popover } from "antd";
import clsx from "clsx";
import Form from "../../../Form";
import NotesModal from "./NotesModal";
import { QuarryDetailsType } from "../../../../Types/quarryDatabaseTypes";
import moment from "moment";
import QuarryDatabaseService from "../../../../services/quarryDatabase.service";
import { convertToFormValuesAndDaySwitchVals } from "../../constants";
import AddQuarryDrawer from "../../AddQuarryDrawer";
import { ModalModes } from "../../../../enums/DatabaseEnums";
import TNLoader from "../../../../reusable-components/TNLoader";

const QuarryDetailsCard = ({
  quarryDetails,
}: {
  quarryDetails: QuarryDetailsType;
}) => {
  const {
    NOTES_ALT,
    EDIT_ALT,
    CLOSED_STATUS,
    OPEN_STATUS,
    REDIRECT_TARGET,
    REL_VALUE,
  } = quarryDetailsConts;
  
  const [currentTime, setCurrentTime] = useState(moment());
  const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false);
  
  // Recalculate based on current time
  const isClosedDay = getIsClosedDay(quarryDetails?.openingHours);
  const hoursItems = getFormattedTimes(quarryDetails?.openingHours);
  const dateTimeValues = getTodaysDateTime(quarryDetails?.openingHours);
  
  const handleNotesModalOpen = () => setNotesModalOpen(true);
  const handleNotesModalClose = () => setNotesModalOpen(false);

  const handleNavigateToAddressLocation = () => {
    window.open(
      getGoogleMapsUrl(quarryDetails?.lat, quarryDetails?.lng),
      REDIRECT_TARGET,
      REL_VALUE,
    );
  };

  // Edit Quarry
  const { EditQuarry } = QuarryDatabaseService();
  const { mutateAsync: editQuarry, isPending: isEditCallPending } =
    EditQuarry();
  const formData = quarryDetails
    ? convertToFormValuesAndDaySwitchVals({
        data: quarryDetails,
      })
    : {};
  const [isEditDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const handleEditDrawerOpen = () => setEditDrawerOpen(true);
  const handleEditDrawerClose = () => setEditDrawerOpen(false);
  useEffect(() => {
    const now = moment();
    const timeouts: NodeJS.Timeout[] = [];
    
    if (dateTimeValues?.open && dateTimeValues?.close) {
      if (now.isBefore(dateTimeValues.open)) {
        const msUntilOpen = dateTimeValues.open.diff(now);
        timeouts.push(setTimeout(() => setCurrentTime(moment()), msUntilOpen));
      }
      
      if (now.isBefore(dateTimeValues.close)) {
        const msUntilClose = dateTimeValues.close.diff(now);
        timeouts.push(setTimeout(() => setCurrentTime(moment()), msUntilClose));
      }
    }
    
    const tomorrow = moment().add(1, 'day').startOf('day');
    const msUntilMidnight = tomorrow.diff(now);
    timeouts.push(setTimeout(() => setCurrentTime(moment()), msUntilMidnight));

    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [currentTime, quarryDetails?.openingHours]);

  return (
    <>
      <TNLoader isLoading={isEditCallPending} />
      <Form
        values={formData?.formValues}
        key={JSON.stringify(formData?.formValues)}
      >
        {() => (
          <AddQuarryDrawer
            isOpen={isEditDrawerOpen}
            handleCloseDrawer={handleEditDrawerClose}
            submitAction={editQuarry}
            submitStatus={isEditCallPending}
            quarryModalMode={ModalModes.EDIT}
            setQuarryModalMode={() => {}}
            quarryId={quarryDetails?.id}
            profileView={true}
          />
        )}
      </Form>
      <Form>
        {() => (
          <NotesModal
            isOpen={notesModalOpen}
            handleClose={handleNotesModalClose}
            quarryId={quarryDetails?.id}
            notesValue={quarryDetails?.notes ?? quarryDetails?.quarryNotes}
          />
        )}
      </Form>
      <div className={styles.detailsCard}>
        <div className={styles.topSection}>
          <div className={styles.mainSection}>
            <div className={styles.headerSection}>
              <div className={styles.mainText}>{quarryDetails?.quarryName}</div>
              <div>
                <span className={styles.additionalText}>
                  {quarryDetails?.quarryId}
                </span>
              </div>
              <div>
                <span className={styles.additionalText}>
                  {quarryDetails?.quarryType}
                </span>
              </div>
            </div>
            <div className={styles.contentSection}>
              <div className={styles.quarryInfo}>
                <img src={locationIcon} />
                <span
                  className={styles.locationName}
                  onClick={() => handleNavigateToAddressLocation()}
                >
                  {quarryDetails?.address ?? quarryDetails?.location}
                </span>
              </div>
              <ConditionalRender visible={!!quarryDetails?.coordinates}>
                <div className={styles.quarryInfo}>
                  <img src={locationIcon} />
                  <span className={styles.detailText}>
                    {quarryDetails?.coordinates?.split(" ")?.join(", ")}
                  </span>
                </div>
              </ConditionalRender>
              <div className={styles.quarryInfo}>
                <img src={phoneIcon} />
                <span className={styles.detailText}>{quarryDetails?.phone}</span>
              </div>
              <ConditionalRender
                visible={quarryDetails?.openingHours?.length > 0}
              >
                <div className={styles.quarryInfo}>
                  <img src={clockIcon} />
                  <div className={styles.openHoursDetails}>
                    <span
                      className={clsx({
                        [styles.closeStatus]: !dateTimeValues?.isOpen,
                        [styles.openStatus]: dateTimeValues?.isOpen,
                      })}
                    >
                      {dateTimeValues?.isOpen ? OPEN_STATUS : CLOSED_STATUS}
                    </span>
                    <span>
                      {dateTimeValues?.isOpen
                        ? dateTimeValues?.closesAt
                        : dateTimeValues?.opensAt}
                    </span>
                  </div>
                  <Popover
                    trigger={PopoverTrigger.HOVER}
                    content={
                      <div className={styles.hoursContainer}>
                        <div className={styles.hoursWrapper}>
                          {hoursItems?.map((item, index) => (
                            <div key={`day-${index}`} className={styles.timeStatus}>
                              {item?.[0]}
                            </div>
                          ))}
                        </div>
                        <div className={styles.hoursWrapper}>
                          {hoursItems?.map((item, index) => (
                            <div key={`time-${index}`} className={styles.timeStatus}>
                              {item?.[1]}
                            </div>
                          ))}
                        </div>
                      </div>
                    }
                    placement={PlacementPositions.BOTTOM}
                  >
                    <div>
                      <img src={downArrow} />
                    </div>
                  </Popover>
                </div>
              </ConditionalRender>
            </div>
          </div>
          <div className={styles.additionalSection}>
            <div
              className={styles.editToggle}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <img src={editIcon} alt={EDIT_ALT} onClick={handleEditDrawerOpen} />
            </div>
          </div>
        </div>
        <div className={styles.bottomSection}>
          <div className={styles.notesToggle} onClick={handleNotesModalOpen}>
            <img src={notesIcon} alt={NOTES_ALT} />
          </div>
          <div className={styles.notesContent}>
            {quarryDetails?.notes ?? quarryDetails?.quarryNotes}
          </div>
        </div>
      </div>
    </>
  );
};

export default QuarryDetailsCard;
